/* Brutalist Tenant Home — concrete, monospace, technical, raw.

   Emotional intent: POWER. Strict 12-column grid. Massive section
   numerals "01" "02" "03" at the left edge. JetBrains Mono throughout.
   ALL CAPS. Square brackets. No rounded anything. No italics. Photos
   tagged with technical metadata ("3 200 × 4 000 PX · ISO 400").
   Section dividers are 1px hard amber lines.

   Structurally distinct: strict grid hero (huge numeral left, photo
   right), services rendered as a monospace TABLE with metadata columns,
   "// SYSTEM_INFO" terminal-style about. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK = "https://images.unsplash.com/photo-1554080353-a576cf803bda?auto=format&fit=crop&w=2000&q=80";

const SectionMark = ({ n, label }) => (
  <div className="flex items-center gap-6 mb-12 pb-4 border-b" style={{ borderColor: "var(--tenant-accent)" }}>
    <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-accent)", fontFamily: "var(--tenant-sans)" }}>
      [{n}]
    </div>
    <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)", fontFamily: "var(--tenant-sans)" }}>
      {label}
    </div>
    <div className="flex-1 h-px" style={{ background: "var(--tenant-accent)" }} />
  </div>
);

export default function BrutalistTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "STUDIO";
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const headline = settings?.hero_title_line1 || "FORM[ ]";
  const subhead = settings?.hero_title_line2 || "FUNCTION[ ]";
  const intro = settings?.hero_description || `${studioName.toUpperCase()} ─ EDITORIAL & ART-DIRECTION PHOTOGRAPHY. STRICT FRAMES. NO POST-PROCESS THEATRICS. DELIVERED ON SCHEDULE.`;
  const aboutBody = settings?.about_body || "We treat every shoot like a spec. Brief in, frames out. No motivational quotes. No 'your story' — your art direction. Files delivered RAW + GRADED within five business days.";
  const port = (settings?.portfolio_images || []).slice(0, 6);

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 4) : [])).catch(() => {});
  }, []);
  useEffect(() => { document.title = `${studioName} // BRUTALIST`; }, [studioName]);

  // Per-template stamps (Brutalist's technical metadata) — editable in
  // Site Settings → Template-specific so a studio can use their actual
  // shoot specs instead of the default "FRAME_001.RAW / ISO 400 / F/2.8".
  const tf = settings?.template_fields?.brutalist || {};
  const frameFilename = (tf.frame_filename || "FRAME_001.RAW").toString();
  const iso = (tf.iso || "400").toString();
  const aperture = (tf.aperture || "F/2.8").toString();
  const shutter = (tf.shutter || "1/500").toString();
  const dimensions = (tf.dimensions || "3200 × 2000 PX").toString();

  if (loading && !settings) return <EditorialSkeleton eyebrow="// loading" />;

  return (
    <div data-testid="brutalist-tenant-home"
      style={{ background: "var(--tenant-bg, #0C0A09)", color: "var(--tenant-fg, #FAFAF8)", fontFamily: "var(--tenant-sans, monospace)" }}>

      {/* ── HERO — strict 12-col grid, oversized "01" numeral ──────────── */}
      <section className="px-6 lg:px-12 pt-16 pb-20" data-testid="brutalist-hero">
        <div className="max-w-[1480px] mx-auto grid grid-cols-12 gap-4 lg:gap-6 items-start">
          {/* Massive numeral 01 spans 3 cols */}
          <div className="col-span-12 lg:col-span-3">
            <div className="leading-[0.8] tabular-nums"
                 style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)", fontSize: "clamp(8rem, 18vw, 14rem)" }}>
              01
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--tenant-fg-soft)" }}>
              [SECTION] : INDEX
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] mt-1" style={{ color: "var(--tenant-fg-soft)" }}>
              [STUDIO]  : {studioName.toUpperCase()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] mt-1" style={{ color: "var(--tenant-fg-soft)" }}>
              [YEAR]    : {new Date().getFullYear()}
            </div>
          </div>
          {/* Photo spans 9 cols */}
          <div className="col-span-12 lg:col-span-9">
            <div className="relative">
              <img src={optimizeImageUrl(heroImg, { w: 2000 })}
                srcSet={optimizedSrcSet(heroImg, [1000, 1500, 2000]) || undefined}
                sizes="(min-width: 1024px) 75vw, 100vw"
                alt={`${studioName} index frame`}
                className="w-full aspect-[16/10] object-cover"
                style={{ objectPosition: heroPos }}
                loading="eager" fetchPriority="high" decoding="async" />
              <div className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.32em] px-2 py-1"
                   style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                {frameFilename}
              </div>
              <div className="absolute bottom-2 right-2 text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
                {dimensions} · ISO {iso} · {aperture}
              </div>
            </div>
            <h1 className="leading-[0.85] mt-8 uppercase"
                style={{ fontFamily: "var(--tenant-serif, monospace)", fontSize: "clamp(2.5rem, 6vw, 5rem)", letterSpacing: "-0.02em" }}>
              {headline} → <span style={{ color: "var(--tenant-accent)" }}>{subhead}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed uppercase" style={{ color: "var(--tenant-fg-soft)", letterSpacing: "0.02em" }}>
              {intro}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={tenantHref("/booking")} data-testid="brutalist-hero-book"
                className="inline-flex items-center gap-2 px-6 py-3 text-[10px] uppercase tracking-[0.32em] font-bold"
                style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                BOOK_NOW [→]
              </Link>
              <Link to={tenantHref("/portfolio")}
                className="inline-flex items-center gap-2 px-6 py-3 text-[10px] uppercase tracking-[0.32em] border"
                style={{ borderColor: "var(--tenant-fg-soft)", color: "var(--tenant-fg-soft)" }}>
                VIEW_PORTFOLIO [→]
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SYSTEM_INFO (about) ──────────────────────────────────────── */}
      <section className="px-6 lg:px-12 py-16">
        <div className="max-w-[1480px] mx-auto">
          <SectionMark n="02" label="// SYSTEM_INFO" />
          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            <div className="col-span-12 lg:col-span-3 text-[10px] uppercase tracking-[0.32em] space-y-1" style={{ color: "var(--tenant-fg-soft)" }}>
              <div>$ studio --about</div>
              <div className="opacity-50">─────────────</div>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <p className="text-base leading-relaxed uppercase max-w-3xl" style={{ letterSpacing: "0.02em" }}>
                {aboutBody}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES — monospace TABLE ───────────────────────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-12 py-16" data-testid="brutalist-services">
          <div className="max-w-[1480px] mx-auto">
            <SectionMark n="03" label="// CATALOG.TBL" />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] uppercase tracking-[0.12em]">
                <thead>
                  <tr className="text-left text-[10px]" style={{ color: "var(--tenant-fg-soft)", borderBottom: "1px solid var(--tenant-accent)" }}>
                    <th className="py-3 pr-4">ID</th>
                    <th className="py-3 pr-4">NAME</th>
                    <th className="py-3 pr-4 hidden md:table-cell">SPEC</th>
                    <th className="py-3 pr-4 text-right">PRICE_FROM</th>
                    <th className="py-3 text-right">─</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc, i) => (
                    <tr key={svc.id} className="border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)" }}>
                      <td className="py-5 pr-4" style={{ color: "var(--tenant-accent)" }}>{String(i + 1).padStart(2, "0")}</td>
                      <td className="py-5 pr-4">{svc.name}</td>
                      <td className="py-5 pr-4 hidden md:table-cell" style={{ color: "var(--tenant-fg-soft)" }}>
                        {svc.duration_minutes ? `${svc.duration_minutes}MIN` : "VARIABLE"}
                      </td>
                      <td className="py-5 pr-4 text-right tabular-nums" style={{ color: "var(--tenant-accent)" }}>
                        ${Number(svc.price_from || svc.starting_price || svc.price || 0).toFixed(0)}
                      </td>
                      <td className="py-5 text-right">
                        <Link to={tenantHref(`/services#${svc.id || svc.slug || ""}`)} className="underline-offset-4 hover:underline">RUN_→</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ── FRAMES — strict grid with technical metadata ─────────────── */}
      {port.length > 0 && (
        <section className="px-6 lg:px-12 py-16">
          <div className="max-w-[1480px] mx-auto">
            <SectionMark n="04" label="// FRAMES" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {port.map((src, i) => (
                <figure key={i} className="relative">
                  <img src={optimizeImageUrl(src, { w: 900 })} alt=""
                    className="w-full aspect-square object-cover"
                    loading="lazy" decoding="async" />
                  <figcaption className="text-[9px] uppercase tracking-[0.32em] mt-2" style={{ color: "var(--tenant-fg-soft)" }}>
                    [{String(i + 1).padStart(3, "0")}] · 4 000 × 4 000 PX
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
